
import Vue, { PropType } from "vue";
import { formatDateTime } from "@/shared/utils/common_utils";
import Note, { NoteType, NoteVisibilityEnum } from "@/features/Notes/Note";
import NoteRequestModel from "@/features/Notes/RequestModels/NoteRequestModel";
import UploadedPlatformDocumentMetadata from "@/features/PlatformDocuments/UploadedPlatformDocumentMetadata";
import FileUpload, { FileUploadRef } from "@/features/Documents/FileUpload.vue";
import FileList, { FileListDocumentType } from "@/features/Documents/FileList.vue";
import { generateUUID } from "@/shared/utils/common_utils";
import { useUserStore } from "@/plugins/pinia";
import { mapStores } from "pinia";
import AppError from "@/shared/components/AppError.vue";

export default Vue.extend({
    name: "NoteSection",
    components: {
        AppError,
        FileUpload,
        FileList,
    },
    data: () => ({
        showForm: false,
        noteText: "",
        documentMetadataIDs: [] as string[],
        uploadedPlatformDocumentMetadatas: [] as UploadedPlatformDocumentMetadata[],
        requestInProgress: false,
        fileListDocumentTypes: FileListDocumentType,
        documentWorkspaceID: null as string | null,
        errorMessage: "",
        showError: false,
    }),
    props: {
        noteType: {
            type: Number as () => NoteType,
            required: true,
        },
        parentID: {
            type: String,
            required: false,
            default: "",
        },
        notes: {
            type: Array as PropType<Array<Note>>,
            default: [] as Note[],
            required: true,
        },
        supplierID: {
            type: String,
            required: false,
            default: "",
        },
    },
    mounted() {
        this.documentWorkspaceID = generateUUID();
    },
    methods: {
        cancelNote(): void {
            this.showForm = false;
            this.noteText = "";
            (this.$refs.fileUpload as FileUploadRef).reset();
        },
        async saveNote(): Promise<void> {
            const newNote = new NoteRequestModel();
            newNote.noteText = this.noteText;
            newNote.platformDocumentMetadataIDs = this.documentMetadataIDs;
            newNote.uploadedPlatformDocumentMetadatas = this.uploadedPlatformDocumentMetadatas;
            newNote.noteType = this.noteType;
            newNote.userID = this.userStore.getUser()?.id ?? "";

            switch (this.noteType) {
                case NoteType.SupplierEvent:
                    newNote.supplierEventID = this.parentID;
                    newNote.visibilityLevel = NoteVisibilityEnum.SupplierSpecificUsers;
                    newNote.visibleSupplierID = this.supplierID;
                    break;
                default:
                    break;
            }

            try {
                const savedNote = await this.$noteService.createNote(newNote);
                this.$emit("noteAdded", savedNote);

                // reset form
                this.showForm = false;
                this.noteText = "";
                this.documentMetadataIDs = [];
                this.uploadedPlatformDocumentMetadatas = [];
                (this.$refs.fileUpload as FileUploadRef).reset();
            } catch (err) {
                this.errorMessage = "Failed to add comment";
                this.showError = true;
            }
        },
        formatDateForDisplay(dateString: string): string {
            const date = new Date(dateString);
            return formatDateTime(date);
        },
        documentUploaded(uploadedPlatformDocumentMetadata: UploadedPlatformDocumentMetadata): void {
            if (!this.uploadedPlatformDocumentMetadatas) {
                this.uploadedPlatformDocumentMetadatas = [];
            }
            this.uploadedPlatformDocumentMetadatas.push(uploadedPlatformDocumentMetadata);
        },
        documentRemoved(removedDocumentMetadataID: string): void {
            this.documentMetadataIDs = this.documentMetadataIDs.filter(id => id != removedDocumentMetadataID);
            this.uploadedPlatformDocumentMetadatas = this.uploadedPlatformDocumentMetadatas.filter(updm => updm.uploadedPlatformDocument.documentID != removedDocumentMetadataID);
        },
        uploadChanged(uploadInProgress: boolean) {
            this.requestInProgress = uploadInProgress;
        },
    },
    computed: {
        ...mapStores(useUserStore),
    },
});
